import {
    cancelShareReward,
    generateShare,
    generateShareReward,
} from "@/api/share";
import router from "@/router";
import { Action, ActionType, Container } from "@/types/customPage";
import { VItem } from "@/types/vItem";
import { validateEmail } from "@/utils/validateEmail";
import { defineStore } from "pinia";
import { authStore } from "./authStore";
import { campaignStore } from "./campaignStore";
import { vItemStore } from "./vItemStore";
import { customComponentStore } from "./customComponentStore";
import { prizeStore } from "./prizeStore";
import { categoryStore } from "./categoryStore";
import { dropPoolStore } from "./dropPoolStore";
import { experienceStore } from "./experienceStore";
import { decrementActionPoints, incrementActionPoints } from "@/api/points";
import { executeApiCall } from "@/api/apiConfig.api";
import {
    findRenderedPhoneInput,
    normalizePhoneNumber,
} from "@/utils/phoneNumber";
import {
    isExactFourDigitPin,
    recoveryMessage,
} from "@/utils/loginRecovery";
import type {
    LoginRecoveryFailure,
    LoginResult,
} from "@/utils/loginRecovery";

const retryTimers = new WeakMap<HTMLElement, number>();

const buttonText = (button: HTMLElement): string =>
    button.innerText || button.textContent || "";

const setButtonText = (button: HTMLElement, text: string): void => {
    button.innerText = text;
    button.textContent = text;
};

const setCodeRequestCooldown = (
    button: HTMLButtonElement,
    retryAfterSeconds: number
): void => {
    const previousTimer = retryTimers.get(button);
    if (previousTimer !== undefined) {
        window.clearInterval(previousTimer);
    }
    const originalText = button.dataset.loginRecoveryText || buttonText(button);
    button.dataset.loginRecoveryText = originalText;
    const retryAt = Date.now() + retryAfterSeconds * 1000;

    const render = () => {
        const remaining = Math.max(0, Math.ceil((retryAt - Date.now()) / 1000));
        if (remaining === 0) {
            const timer = retryTimers.get(button);
            if (timer !== undefined) {
                window.clearInterval(timer);
                retryTimers.delete(button);
            }
            button.disabled = false;
            setButtonText(button, originalText);
            return;
        }
        button.disabled = true;
        setButtonText(button, `TRY AGAIN IN ${remaining}s`);
    };

    render();
    retryTimers.set(button, window.setInterval(render, 1000));
};

const handlePinRecovery = async (
    result: LoginRecoveryFailure,
    pinInput: HTMLInputElement
): Promise<void> => {
    if (
        result.recoveryAction === "RETRY_PIN" ||
        result.recoveryAction === "REQUEST_NEW_CODE" ||
        result.recoveryAction === "RESTART_SIGN_IN"
    ) {
        pinInput.value = "";
    }
    alert(recoveryMessage(result));
    if (result.recoveryAction === "RESTART_SIGN_IN") {
        const campaignId = authStore().campaignId || "";
        await router.replace(`/${campaignId}/sign-in`).catch(() => {
            // The user can retry the navigation from the current auth page.
        });
    }
};

interface INavigationStoreState {
    action: Action;
    currentAction: Action;
    previousAction: Action;
    popupVisible: boolean;
    selectedPopup: Container;
    inARView: boolean;
    showFaceCard: boolean;
    deniedPermissions: [];
    loading: boolean;
    watchId: number | null;
    poorAccuracy: boolean;
    geolocationAllowed: boolean;
    loadingTransparent: boolean;
}

export const resolveGoToUrlDestination = (
    rawUrl: string,
    currentOrigin: string,
    estateBaseDomain: string | undefined
): string => {
    const existingDestination = "//" + rawUrl;
    const normalizedEstateBaseDomain = estateBaseDomain
        ?.trim()
        .toLowerCase()
        .replace(/\.$/, "");

    if (!normalizedEstateBaseDomain) {
        return existingDestination;
    }

    try {
        const targetUrl = new URL(
            /^https?:\/\//i.test(rawUrl) ? rawUrl : `https://${rawUrl}`
        );
        const isReactExperiencePath =
            targetUrl.pathname === "/x" || targetUrl.pathname.startsWith("/x/");
        const isCanonicalEstateApp =
            targetUrl.hostname.toLowerCase() ===
            `app.${normalizedEstateBaseDomain}`;

        if (isReactExperiencePath && isCanonicalEstateApp) {
            return new URL(
                `${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`,
                currentOrigin
            ).href;
        }
    } catch {
        // Preserve the existing protocol-relative navigation for malformed CMS values.
    }

    return existingDestination;
};

export const navigationStore = defineStore("navigationStore", {
    persist: false,
    state: () =>
        ({
            action: {},
            currentAction: {},
            previousAction: {},
            popupVisible: false,
            selectedPopup: {},
            inARView: false,
            showFaceCard: false,
            deniedPermissions: [],
            loading: false,
            watchId: null,
            poorAccuracy: true,
            geolocationAllowed: false,
            loadingTransparent: false,
        } as INavigationStoreState),

    getters: {
        getAction(state: INavigationStoreState): Action {
            return state.action;
        },
        getCurrentAction(state: INavigationStoreState): Action {
            return state.currentAction;
        },
        getPreviousAction(state: INavigationStoreState): Action {
            return state.previousAction;
        },
        getPopupVisible(state: INavigationStoreState): boolean {
            return state.popupVisible;
        },
        getSelectedPopup(state: INavigationStoreState): Container {
            return state.selectedPopup;
        },
        getShowFaceCard(state: INavigationStoreState): boolean {
            return state.showFaceCard;
        },
        getLoading(state: INavigationStoreState): boolean {
            return state.loading;
        },
        getDeniedPermissions(state: INavigationStoreState): string[] {
            return state.deniedPermissions;
        },
        getPoorAccuracy(state: INavigationStoreState): boolean {
            return state.poorAccuracy;
        },
        getLoadingTransparent(state: INavigationStoreState): boolean {
            return state.loadingTransparent;
        },
    },
    actions: {
        async emitAction(action: Action) {
            if (action?.enableActionPoints && action?.actionPoints) {
                try {
                    await incrementActionPoints(
                        authStore().campaignId!,
                        authStore().userId!,
                        action.actionPoints
                    );
                } catch (error) {
                    console.error(error);
                }
            }
            if (action.type != "none") {
                this.popupVisible = false;
                switch (action.type) {
                    case ActionType.ACTION_POINT_INCREMENT: {
                        try {
                            await incrementActionPoints(
                                authStore().campaignId!,
                                authStore().userId!,
                                action.actionPoints ?? 0
                            );
                        } catch (error) {
                            console.error(error);
                        }
                        break;
                    }
                    case ActionType.ACTION_POINT_DECREMENT: {
                        try {
                            await decrementActionPoints(
                                authStore().campaignId!,
                                authStore().userId!,
                                action.actionPoints ?? 0
                            );
                        } catch (error) {
                            console.error(error);
                        }
                        break;
                    }
                    case ActionType.API_CALL: {
                        try {
                            const campaignId = authStore().campaignId || "";
                            const userId = authStore().userId || undefined;
                            const apiConfigID = action.value || "";
                            await executeApiCall(
                                campaignId,
                                apiConfigID,
                                userId
                            );
                        } catch (error) {
                            console.error(error);
                        }
                        break;
                    }
                    case ActionType.GO_TO_PAGE: {
                        console.log(
                            `navigationStore: go-to-page action received ${action.value}`
                        );
                        try {
                            // Proceed to the target page if permissions and location checks pass
                            const campaignId = authStore().campaignId || "";
                            const targetRouteName =
                                action.value == "map"
                                    ? "Map"
                                    : action.value == "ar-viewer"
                                      ? "AR"
                                      : action.value == "vault"
                                        ? "Wallet"
                                        : null;
                            if (targetRouteName) {
                                experienceStore().resolveFromState({
                                    routeName: targetRouteName,
                                    pools: dropPoolStore().getPools,
                                    pendingItems: vItemStore().getPendingItems,
                                });
                            }
                            if (action.value == "map") {
                                const needsGps = experienceStore().requiresGpsLocation;
                                console.log("[NavStore] GO_TO_PAGE map", {
                                    requiresGps: needsGps,
                                    experienceKind: experienceStore().experienceKind,
                                    preferredDropType: experienceStore().preferredDropType,
                                });
                                if (needsGps) {
                                    this.loading = true;
                                    this.startWatchingPosition();
                                } else {
                                    await router.push(`/${campaignId}/map`);
                                }
                            } else {
                                const page = action.value!.replace(/^\/+/, '');
                                await router.push(
                                    `/${campaignId}/${page}`
                                );
                            }
                        } catch (err) {
                            console.error(err);
                        }
                        break;
                    }

                    case ActionType.GO_TO_URL: {
                        const urlAndTarget = action.value!.split(",");
                        const url = urlAndTarget[0].split("url=")[1];
                        const target = urlAndTarget[1].split("target=")[1];
                        const destination = resolveGoToUrlDestination(
                            url,
                            window.location.origin,
                            import.meta.env.VITE_APP_ESTATE_BASE_DOMAIN
                        );
                        if (target == "_blank") {
                            window.open(destination, "_blank");
                        } else {
                            window.location.replace(destination);
                        }
                        break;
                    }
                    case ActionType.SHARE_LINK: {
                        try {
                            let dropPoolId: string | null = null;
                            if (action.value) {
                                dropPoolId = action.value;
                            }
                            const campaignId = authStore().campaignId || "";
                            const userId = authStore().userId || "";
                            const url = await generateShare(
                                campaignId,
                                userId,
                                dropPoolId
                            );

                            const data = {
                                url:
                                    url ||
                                    `${window.location.origin}/${
                                        window.location.pathname.split("/")[1]
                                    }`,
                            };

                            if (
                                navigator.canShare &&
                                navigator.canShare(data)
                            ) {
                                await navigator.share(data);
                                // if (url) {
                                // 	await navigator.share({
                                // 		url: url,
                                // 	});
                                // } else {
                                // 	// fallback url
                                // 	await navigator.share({
                                // 		url: `${window.location.origin}/${
                                // 			window.location.pathname.split("/")[1]
                                // 		}`,
                                // 	});
                                // }
                                await campaignStore().shareCampaign();
                            } else {
                                alert("browser doesn't support share");
                            }
                        } catch (error) {
                            console.error(error);
                        }
                        break;
                    }
                    case ActionType.SHARE_REWARD: {
                        try {
                            const vatID =
                                vItemStore().getSelectedItem!.id || null;
                            const vat = vItemStore().getSelectedItem;

                            if (!vatID && !vat) {
                                return;
                            }
                            const category = categoryStore().getItemByID(
                                vat?.categoryId!
                            );

                            if (!category) {
                                return;
                            }

                            const campaignId = authStore().campaignId || "";
                            const userId = authStore().userId || "";

                            const shareId = await generateShareReward(
                                campaignId,
                                userId,
                                vatID
                            );

                            console.log(`?shareRewardCode=${shareId}`);

                            const data = {
                                url: `${window.location.origin}/${
                                    window.location.pathname.split("/")[1]
                                }?shareRewardCode=${shareId}`,
                                text: category?.sharePrizeForRewardText || "",
                            };
                            console.log(data);
                            if (
                                navigator.canShare &&
                                navigator.canShare(data)
                            ) {
                                await navigator.share(data);
                            } else {
                                alert("browser doesn't support share");
                            }
                        } catch (error) {
                            console.error(error);
                        }
                        break;
                    }
                    case ActionType.CANCEL_SHARE_REWARD: {
                        try {
                            const vat = vItemStore().getSelectedItem || null;
                            if (!vat) {
                                return;
                            }
                            const campaignId = authStore().campaignId || "";
                            await cancelShareReward(campaignId, vat.shareId!);
                        } catch (error) {
                            console.error(error);
                        }
                        break;
                    }
                    case ActionType.RESHARE_REWARD: {
                        const vat = vItemStore().getSelectedItem || null;
                        if (!vat) {
                            return;
                        }
                        const data = {
                            url: `${window.location.origin}/${
                                window.location.pathname.split("/")[1]
                            }?shareRewardCode=${vat.shareId}`,
                        };
                        console.log(data);
                        if (navigator.canShare && navigator.canShare(data)) {
                            await navigator.share(data);
                        } else {
                            alert("browser doesn't support share");
                        }
                        break;
                    }
                    case ActionType.SHOW_FACECARD: {
                        try {
                            const campaignId = authStore().campaignId || "";
                            await router.push(`/${campaignId}/vault`);
                        } catch (err) {
                            console.error(err);
                        }
                        break;
                    }

                    case ActionType.LOG_OUT: {
                        authStore().logout();
                        break;
                    }

                    case ActionType.RESEND_PIN: {
                        const email = authStore().email;
                        const resendButton = document.querySelector<HTMLButtonElement>(
                            "#resend-pin"
                        );
                        const originalText = resendButton ? buttonText(resendButton) : "";
                        let result: LoginResult | undefined;
                        try {
                            if (resendButton) {
                                resendButton.disabled = true;
                                setButtonText(resendButton, "SENDING NEW CODE...");
                            }
                            this.setloading(true);
                            result = await authStore().signinLoginCode({ email });
                            if (!result.success) {
                                alert(recoveryMessage(result));
                                if (result.recoveryAction === "RESTART_SIGN_IN") {
                                    const campaignId = authStore().campaignId || "";
                                    await router.replace(`/${campaignId}/sign-in`).catch(() => {
                                        // The current auth page remains usable.
                                    });
                                }
                            }
                        } catch (error) {
                            console.error("login code resend failed", {
                                errorType: error instanceof Error ? error.name : "UnknownError",
                            });
                            alert("Sign-in is temporarily unavailable. Please try again shortly.");
                        } finally {
                            this.setloading(false);
                            if (resendButton) {
                                resendButton.disabled = false;
                                setButtonText(resendButton, originalText);
                            }
                        }
                        if (
                            resendButton &&
                            result &&
                            !result.success &&
                            result.recoveryAction === "WAIT"
                        ) {
                            setCodeRequestCooldown(
                                resendButton,
                                result.retryAfterSeconds || 1
                            );
                        }
                        break;
                    }

                    case ActionType.OPEN_POPUP: {
                        const item = vItemStore().getSelectedItem;
                        const component =
                            customComponentStore().getCustomComponentByName(
                                action.value!
                            );
                        if (component) {
                            this.selectedPopup = component.container;
                            this.popupVisible = true;
                            if (item) {
                                populateVoucherCode(item);
                            }
                        } else {
                            alert(`Popup does not exist for ${action.value}`);
                        }
                        break;
                    }

                    case ActionType.CLOSE_POPUP: {
                        this.popupVisible = false;
                        break;
                    }

                    case ActionType.REDEEM_ITEM: {
                        this.setloading(true);
                        try {
                            const item = vItemStore().getSelectedItem;
                            const prize = prizeStore().getPrizeByID(item!.prizeId!);
                            const response = await vItemStore().redeemItem({
                                itemID: item!.id,
                                merchantWalletId: prize!.redemptionWalletID,
                            });

                            if (response && response.success) {
                                await vItemStore().getItems();
                                if (prize!.redemptionType === "voucher") {
                                    await this.emitAction({
                                        type: ActionType.OPEN_POPUP,
                                        value: "redeem-voucher-success",
                                    });
                                } else {
                                    await this.emitAction({
                                        type: ActionType.OPEN_POPUP,
                                        value: "tap-to-redeem-success",
                                    });
                                }
                            }
                        } catch (error) {
                            console.error(error);
                            await this.emitAction({
                                type: ActionType.OPEN_POPUP,
                                value: "general-error",
                            });
                        } finally {
                            this.setloading(false);
                        }
                        break;
                    }
                    case ActionType.REDEEM_MOMINT_NFT: {
                        const item = vItemStore().getSelectedItem;

                        const prize = prizeStore().getPrizeByID(item!.prizeId!);
                        this.setloading(true);
                        const response = await vItemStore().redeemMomintNFT({
                            merchantWalletId: prize!.redemptionWalletID,
                            itemID: item!.id || "",
                        });

                        this.setloading(false);

                        if (response?.success) {
                            await vItemStore().getItems();
                            this.showFaceCard = false;
                            this.emitAction({
                                type: ActionType.OPEN_POPUP,
                                value: "redeem-momint-nft-success",
                            });
                        }
                        break;
                    }

                    case ActionType.REDEEM_URL_ITEM: {
                        try {
                            const item = vItemStore().getSelectedItem;
                            const prize = prizeStore().getPrizeByID(item!.prizeId!);
                            await vItemStore().redeemItem({
                                itemID: item!.id,
                                merchantWalletId: prize!.redemptionWalletID,
                            });

                            await vItemStore().getItems();
                            this.saveShowFaceCard(false);
                            const urlAndTarget = action.value!.split(",");
                            let url = urlAndTarget[0].split("url=")[1];
                            const target = urlAndTarget[1].split("target=")[1];
                            url = url.replace(
                                "{voucher-code}",
                                item!.voucherCode || ""
                            );
                            if (target == "_blank") {
                                window.open("//" + url, "_blank");
                            } else {
                                window.location.replace("//" + url);
                            }
                        } catch (error) {
                            console.error(error);
                            await this.emitAction({
                                type: ActionType.OPEN_POPUP,
                                value: "general-error",
                            });
                        }
                        break;
                    }

                    case ActionType.SIGNUP: {
                        const emailInput =
                            document.querySelector<HTMLInputElement>("#email");
                        const signupButton =
                            document.querySelector<HTMLInputElement>(
                                "#sign-up"
                            );
                        const acceptTerms =
                            document.querySelector<HTMLInputElement>(
                                "#accept-terms"
                            );

                        const firstName =
                            document.querySelector<HTMLInputElement>(
                                "#first-name"
                            );
                        const lastName =
                            document.querySelector<HTMLInputElement>(
                                "#last-name"
                            );
                        const mobileNumber = findRenderedPhoneInput();
                        const brandPreference =
                            document.querySelector<HTMLInputElement>(
                                "#brand-preference"
                            );
                        const secondaryCheckbox =
                            document.querySelector<HTMLInputElement>(
                                "#secondary-checkbox"
                            );
                        const emailOptInCheckbox =
                            document.querySelector<HTMLInputElement>(
                                "#email-opt-in"
                            );
                        const year =
                            document.querySelector<HTMLSelectElement>(
                                "#dob-year"
                            );
                        const month =
                            document.querySelector<HTMLSelectElement>(
                                "#dob-month"
                            );
                        const day =
                            document.querySelector<HTMLSelectElement>(
                                "#dob-day"
                            );

                        const dob: { [key: string]: string | null } = {
                            year: null,
                            month: null,
                            day: null,
                        };

                        const regObject: any = {};
                        if (emailInput && emailInput?.value.length == 0) {
                            alert("Please add an email address");
                            return;
                        } else if (emailInput) {
                            regObject["email"] = emailInput.value;
                        }

                        if (firstName && firstName?.value.length == 0) {
                            alert("Please input your first name");
                            return;
                        } else if (firstName) {
                            regObject["firstName"] = firstName.value;
                        } else {
                            regObject["firstName"] = "";
                        }
                        if (lastName && lastName?.value.length == 0) {
                            alert("Please input your last name");
                            return;
                        } else if (lastName) {
                            regObject["lastName"] = lastName.value;
                        } else {
                            regObject["lastName"] = "";
                        }
                        const normalizedMobileNumber = mobileNumber
                            ? normalizePhoneNumber(mobileNumber.value)
                            : undefined;
                        if (mobileNumber && !normalizedMobileNumber) {
                            alert("Please input a valid mobile number");
                            return;
                        } else if (mobileNumber) {
                            regObject["mobileNumber"] = normalizedMobileNumber;
                        } else {
                            regObject["mobileNumber"] = "";
                        }
                        if (
                            brandPreference &&
                            brandPreference?.value.length == 0
                        ) {
                            alert("Please input your brand preference");
                            return;
                        } else if (brandPreference) {
                            regObject["brandPreference"] =
                                brandPreference.value;
                        } else {
                            regObject["brandPreference"] = "";
                        }
                        if (secondaryCheckbox && !secondaryCheckbox.checked) {
                            alert("Please check the box to continue");
                            return;
                        } else if (secondaryCheckbox) {
                            regObject["secondaryCheckbox"] =
                                secondaryCheckbox.checked;
                        } else {
                            regObject["secondaryCheckbox"] = false;
                        }
                        if (emailOptInCheckbox) {
                            regObject["emailOptIn"] =
                                emailOptInCheckbox.checked;
                        } else {
                            regObject["emailOptIn"] = false;
                        }
                        if (acceptTerms && !acceptTerms?.checked) {
                            alert("Please accept the terms and conditions");
                            return;
                        } else if (acceptTerms) {
                            regObject["termsAndConditionsCheck"] =
                                acceptTerms.checked;
                            authStore().recordTermsAcceptance(acceptTerms.checked);
                        } else {
                            regObject["termsAndConditionsCheck"] = true;
                            authStore().recordTermsAcceptance(true);
                        }
                        //Year
                        if (year && year?.value == "Year") {
                            alert("Please input your year of birth");
                            return;
                        } else if (year) {
                            dob.year = year.value;
                        }
                        //Month
                        if (month && month?.value == "Month") {
                            alert("Please input your month of birth");
                            return;
                        } else if (month) {
                            dob.month = month.value;
                        }
                        //Day
                        if (day && day?.value == "Day") {
                            alert("Please input your day of birth");
                            return;
                        } else if (day) {
                            dob.day = day.value;
                        }

                        const email = emailInput?.value.trim();

                        if (email && validateEmail(email)) {
                            signupButton?.setAttribute("disabled", "disabled");
                            if (dob.year && dob.month && dob.day) {
                                regObject[
                                    "dateOfBirth"
                                ] = `${dob.day}-${dob.month}-${dob.year}`;
                                regObject["yearOfBirth"] = `${dob.year}`;
                            } else {
                                regObject["dateOfBirth"] = "";
                                regObject["yearOfBirth"] = "";
                            }
                            const originText = signupButton?.innerText;
                            signupButton!.innerText = "SIGNING IN...";
                            const result = await authStore().registerLoginCode(
                                regObject
                            );

                            if (signupButton && originText) {
                                signupButton!.innerText = originText;
                            }

                            signupButton?.removeAttribute("disabled");
                            if (result) {
                                const campaignId = authStore().campaignId || "";
                                router
                                    .push(`/${campaignId}/enter-pin`)
                                    .catch(() => {
                                        //
                                    });
                            }
                        } else {
                            alert("Please enter a valid email address.");
                        }
                        break;
                    }

                    case ActionType.SIGNIN_REQUEST_CODE: {
                        const emailInput =
                            document.querySelector<HTMLInputElement>("#email");
                        const signInBtn =
                            document.querySelector<HTMLButtonElement>(
                                "#signin-btn"
                            );
                        const acceptTerms =
                            document.querySelector<HTMLInputElement>(
                                "#accept-terms"
                            );

                        const regObject: any = {};
                        if (emailInput && emailInput?.value.length == 0) {
                            alert("Please add an email address");
                            return;
                        } else if (emailInput) {
                            regObject["email"] = emailInput.value;
                        }

                        if (acceptTerms && !acceptTerms?.checked) {
                            alert("Please accept the terms and conditions");
                            return;
                        }
                        if (acceptTerms?.checked) {
                            authStore().recordTermsAcceptance(true);
                        }

                        const email = emailInput?.value.trim();

                        if (email && validateEmail(email)) {
                            let originText = "";
                            let result: LoginResult | undefined;
                            let rateLimitSeconds: number | undefined;
                            if (signInBtn) {
                                signInBtn?.setAttribute("disabled", "disabled");
                                originText = buttonText(signInBtn);
                                setButtonText(signInBtn, "SIGNING IN...");
                            } else {
                                alert(
                                    "signInBtn not found, make sure you have an element with the id 'signin-btn'"
                                );
                            }
                            try {
                                this.setloading(true);
                                result = await authStore().signinLoginCode(regObject);
                                if (result.success) {
                                    const campaignId = authStore().campaignId || "";
                                    await router
                                        .push(`/${campaignId}/enter-pin`)
                                        .catch(() => {
                                            //
                                        });
                                } else {
                                    alert(recoveryMessage(result));
                                    if (result.recoveryAction === "RESTART_SIGN_IN") {
                                        const campaignId = authStore().campaignId || "";
                                        await router.replace(`/${campaignId}/sign-in`).catch(() => {
                                            // The current sign-in page remains usable.
                                        });
                                    }
                                    if (result.recoveryAction === "WAIT") {
                                        rateLimitSeconds = result.retryAfterSeconds || 1;
                                    }
                                }
                            } catch (error) {
                                console.error("login code request failed", {
                                    errorType: error instanceof Error ? error.name : "UnknownError",
                                });
                                alert("Sign-in is temporarily unavailable. Please try again shortly.");
                            } finally {
                                this.setloading(false);
                                if (signInBtn) {
                                    setButtonText(signInBtn, originText);
                                    signInBtn.removeAttribute("disabled");
                                }
                            }
                            if (signInBtn && rateLimitSeconds !== undefined) {
                                setCodeRequestCooldown(signInBtn, rateLimitSeconds);
                            }
                        } else {
                            alert("Please enter a valid email address.");
                        }
                        break;
                    }

                    case ActionType.SUBMIT_PIN: {
                        const pinInput = document.querySelector<HTMLInputElement>("#pin");
                        const pin = pinInput?.value || "";

                        const submitButton =
                            document.querySelector<HTMLButtonElement>(
                                "#submit-pin"
                            );

                        if (!pinInput || !isExactFourDigitPin(pin)) {
                            alert("Invalid pin, please try again");
                            if (pinInput) {
                                pinInput.value = "";
                            }
                            return;
                        }

                        const originText = submitButton ? buttonText(submitButton) : "";
                        try {
                            submitButton?.setAttribute("disabled", "disabled");
                            if (submitButton) {
                                setButtonText(submitButton, "LOGGING IN...");
                            }
                            this.setloading(true);
                            const result = await authStore().login(Number(pin));
                            if (result.success) {
                                const campaign = campaignStore().getItem;
                                let pageAfterAuth = campaign?.pageAfterAuth;
                                if (!pageAfterAuth || pageAfterAuth == "") {
                                    pageAfterAuth = "vault";
                                }
                                authStore().savePageAfterAuth(true);
                                const campaignId = authStore().campaignId || "";
                                await router
                                    .push(`/${campaignId}/${pageAfterAuth}`)
                                    .catch(() => {
                                        //
                                    });
                            } else {
                                await handlePinRecovery(result, pinInput);
                            }
                        } catch (error) {
                            console.error("login submission failed", {
                                errorType: error instanceof Error ? error.name : "UnknownError",
                            });
                            alert("Sign-in is temporarily unavailable. Please try again shortly.");
                        } finally {
                            this.setloading(false);
                            if (submitButton) {
                                setButtonText(submitButton, originText);
                                submitButton.removeAttribute("disabled");
                            }
                        }
                        break;
                    }
                    case ActionType.COPY_TO_CLIPBOARD: {
                        const copyText = document.getElementById(
                            action.value!
                        ) as any;
                        copyToClipboard(copyText.innerText);
                        break;
                    }

                    default:
                        break;
                }

                this.action = action;
                this.previousAction = action;
                setTimeout(() => {
                    this.action = {
                        type: ActionType.NONE,
                        value: "",
                        index: 0,
                    };
                }, 10);
            }
        },
        saveAction(action: Action) {
            this.previousAction = action;
        },
        isInARView() {
            this.inARView = true;
        },
        saveShowFaceCard(value: boolean) {
            console.log(
                `navigationStore: saveShowFaceCard called with value: ${value}`
            );
            this.showFaceCard = value;
        },
        setloading(value: boolean) {
            this.loading = value;
        },
        setPoorAccuracy(value: boolean) {
            this.poorAccuracy = value;
        },
        saveDeniedPermissions(deniedPermission: never) {
            if (!this.deniedPermissions.includes(deniedPermission)) {
                this.deniedPermissions.push(deniedPermission);
            }
        },
        clearDeniedPermissions() {
            this.deniedPermissions = [];
        },
        startWatchingPosition() {
            if (!experienceStore().requiresGpsLocation) {
                console.log("[NavStore] startWatchingPosition skipped - GPS not required");
                this.stopWatchingPosition();
                return;
            }
            this.stopWatchingPosition();
            this.geolocationAllowed = false;

            if (!("geolocation" in navigator)) {
                console.error("[NavStore] navigator.geolocation not available");
                this.loading = false;
                return;
            }

            console.log("[NavStore] startWatchingPosition: calling watchPosition");

            let receivedCallback = false;
            const safetyTimeoutMs = 15000;

            const safetyTimer = window.setTimeout(() => {
                if (!receivedCallback) {
                    console.error("[NavStore] watchPosition SILENT FAILURE - no callback after " + safetyTimeoutMs + "ms.",
                        "iOS may have silently blocked geolocation.",
                        "Check: Settings > Privacy > Location Services > Safari Websites");
                    this.stopWatchingPosition();
                    this.loading = false;
                    this.emitAction({
                        type: ActionType.GO_TO_PAGE,
                        value: "permissions",
                    });
                }
            }, safetyTimeoutMs);

            const options = {
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 2000,
            };

            this.watchId = navigator.geolocation.watchPosition(
                (position) => {
                    receivedCallback = true;
                    window.clearTimeout(safetyTimer);
                    const latitude = position.coords.latitude;
                    const longitude = position.coords.longitude;
                    const accuracy = position.coords.accuracy;

                    console.log("[NavStore] watchPosition SUCCESS", {
                        lat: latitude, lng: longitude, accuracy: accuracy,
                        geolocationAllowed: this.geolocationAllowed,
                    });

                    dropPoolStore().saveUserLocation([longitude, latitude]);
                    dropPoolStore().saveLocationAccuracy(accuracy);
                    authStore().saveLastKnownGps({
                        lat: latitude,
                        lon: longitude,
                        accuracy,
                    });
                    this.loading = false;
                    if (!this.geolocationAllowed) {
                        this.geolocationAllowed = true;
                        router.push(`/${authStore().getCampaignID}/map`);
                    }
                },
                (error) => {
                    receivedCallback = true;
                    window.clearTimeout(safetyTimer);
                    const errorNames: Record<number, string> = {
                        1: "PERMISSION_DENIED",
                        2: "POSITION_UNAVAILABLE",
                        3: "TIMEOUT",
                    };
                    console.error("[NavStore] watchPosition FAILED", {
                        code: error.code,
                        name: errorNames[error.code],
                        message: error.message,
                    });

                    this.poorAccuracy = true;
                    this.clearDeniedPermissions();
                    this.stopWatchingPosition();
                    this.loading = false;
                    if (error.code == 1) {
                        this.saveDeniedPermissions([
                            "Geolocation",
                        ] as never);
                    }
                    this.emitAction({
                        type: ActionType.GO_TO_PAGE,
                        value: "permissions",
                    });
                },
                options
            );
        },
        stopWatchingPosition() {
            if (this.watchId !== null) {
                console.log("[NavStore] stopWatchingPosition: clearing watchId", this.watchId);
                navigator.geolocation.clearWatch(this.watchId);
                this.watchId = null;
            }
            this.geolocationAllowed = false;
        },
        setLoadingTransparent(value: boolean) {
            this.loadingTransparent = value;
        },
    },
});

function populateVoucherCode(selectedItem: VItem | undefined) {
    setTimeout(() => {
        if (!selectedItem) {
            return;
        }
        const voucherCodes = document.querySelectorAll(".voucher-code") as any;
        const voucherCode = document.querySelector(
            "#voucher-code"
        ) as HTMLElement;

        if (voucherCode) {
            voucherCode.innerText = selectedItem!.voucherCode!;
        }
        if (voucherCodes) {
            for (let i = 0; i < voucherCodes.length; i++) {
                voucherCodes[i].innerText = selectedItem!.voucherCode!;
            }
        }
    }, 100);
}
function copyToClipboard(text: string) {
    navigator.clipboard
        .writeText(text)
        .then(() => {
            alert("Successfully copied to clipboard");
        })
        .catch((err) => {
            console.error("Unable to copy text to clipboard", err);
        });
}
